// src/services/BandwidthLimitedDownloadService.js
import { storage } from '../firebase';
import { ref, getMetadata, getDownloadURL, listAll } from 'firebase/storage';
import DocumentService from './DocumentService';

/**
 * Service to handle document downloads with bandwidth limiting
 */
const BandwidthLimitedDownloadService = {
  // Constants
  DAILY_BACKGROUND_LIMIT: 1 * 1024 * 1024, // 1MB in bytes
  STORAGE_KEY_USAGE: 'bandwidth_usage',
  STORAGE_KEY_DATE: 'bandwidth_date',
  
  // Download queue for background processing
  downloadQueue: [],
  isProcessingQueue: false,
  
  /**
   * Initialize the download tracker
   */
  initialize: () => {
    // Reset daily tracker if it's a new day
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const lastTrackedDay = localStorage.getItem(BandwidthLimitedDownloadService.STORAGE_KEY_DATE);
    
    if (lastTrackedDay !== today) {
      localStorage.setItem(BandwidthLimitedDownloadService.STORAGE_KEY_USAGE, '0');
      localStorage.setItem(BandwidthLimitedDownloadService.STORAGE_KEY_DATE, today);
    }
    
    // Set up idle callback for background downloads
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(BandwidthLimitedDownloadService.processDownloadQueue);
    }
    
    // Check if auto-caching is enabled and start the background process if needed
    const autoCaching = localStorage.getItem('autoCaching');
    if (autoCaching === 'true') {
      BandwidthLimitedDownloadService.startBackgroundCaching();
    }
  },
  
  /**
   * Start background caching process if auto-caching is enabled
   */
  startBackgroundCaching: async () => {
    const autoCaching = localStorage.getItem('autoCaching');
    if (autoCaching !== 'true') {
      console.log('[BandwidthLimitedDownloadService] Auto-caching is disabled.');
      return;
    }
    
    console.log('[BandwidthLimitedDownloadService] Starting background caching.');
    
    try {
      // Get a list of root sections for background caching
      const rootFolderRef = ref(storage, 'Firebase_upload/Master Vehicle File');
      const rootResult = await listAll(rootFolderRef);
      
      // Queue documents from each section (but don't download immediately)
      for (const prefix of rootResult.prefixes) {
        BandwidthLimitedDownloadService.queueSectionForBackgroundDownload(prefix.fullPath);
      }
    } catch (error) {
      console.error('[BandwidthLimitedDownloadService] Error starting background caching:', error);
    }
  },
  
  /**
   * Queue a section for background downloading
   * @param {string} sectionPath Path to the section
   */
  queueSectionForBackgroundDownload: async (sectionPath) => {
    console.log(`[BandwidthLimitedDownloadService] Queueing section: ${sectionPath}`);
    
    try {
      // Get documents for the section
      const documents = await DocumentService.fetchDocumentsBySection(sectionPath);
      
      // Queue each document that isn't already cached
      for (const doc of documents) {
        try {
          const isCached = await DocumentService.isDocumentCached(doc.id);
          if (!isCached) {
            BandwidthLimitedDownloadService.queueForDownload(doc);
          }
        } catch (error) {
          console.error(`[BandwidthLimitedDownloadService] Error checking cache status: ${doc.name}`, error);
        }
      }
    } catch (error) {
      console.error(`[BandwidthLimitedDownloadService] Error queueing section: ${sectionPath}`, error);
    }
  },
  
  /**
   * Get current bandwidth usage for today
   * @returns {number} Bytes used today
   */
  getCurrentUsage: () => {
    return parseInt(localStorage.getItem(BandwidthLimitedDownloadService.STORAGE_KEY_USAGE) || '0');
  },
  
  /**
   * Update bandwidth usage tracker
   * @param {number} bytes Additional bytes to record
   */
  trackUsage: (bytes) => {
    const currentUsage = BandwidthLimitedDownloadService.getCurrentUsage();
    const newUsage = currentUsage + bytes;
    localStorage.setItem(BandwidthLimitedDownloadService.STORAGE_KEY_USAGE, newUsage.toString());
    console.log(`[BandwidthLimitedDownloadService] Updated usage: ${newUsage} bytes (${(newUsage / (1024 * 1024)).toFixed(2)} MB)`);
  },
  
  /**
   * Check if we're under the daily background download limit
   * @param {number} requiredBytes Bytes needed for next download
   * @returns {boolean} True if download is allowed
   */
  canDownloadInBackground: (requiredBytes) => {
    const currentUsage = BandwidthLimitedDownloadService.getCurrentUsage();
    return (currentUsage + requiredBytes) <= BandwidthLimitedDownloadService.DAILY_BACKGROUND_LIMIT;
  },
  
  /**
   * Add document to background download queue
   * @param {Object} document Document metadata
   */
  queueForDownload: (document) => {
    // Check if auto-caching is disabled
    const autoCaching = localStorage.getItem('autoCaching');
    if (autoCaching === 'false') {
      // Don't queue if auto-caching is disabled
      return;
    }
    
    // Check if already in queue
    const existingIndex = BandwidthLimitedDownloadService.downloadQueue.findIndex(
      item => item.id === document.id
    );
    
    if (existingIndex >= 0) {
      // Already queued, update with latest info
      BandwidthLimitedDownloadService.downloadQueue[existingIndex] = document;
    } else {
      // Add to queue
      BandwidthLimitedDownloadService.downloadQueue.push(document);
      console.log(`[BandwidthLimitedDownloadService] Added to queue: ${document.name}`);
    }
    
    // Start processing if not already running
    if (!BandwidthLimitedDownloadService.isProcessingQueue && 'requestIdleCallback' in window) {
      window.requestIdleCallback(BandwidthLimitedDownloadService.processDownloadQueue);
    }
  },
  
  /**
   * Clear the download queue
   */
  clearQueue: () => {
    BandwidthLimitedDownloadService.downloadQueue = [];
    console.log('[BandwidthLimitedDownloadService] Download queue cleared');
  },
  
  /**
   * Process download queue during browser idle time
   */
  processDownloadQueue: async (deadline) => {
    // Check if auto-caching is disabled
    const autoCaching = localStorage.getItem('autoCaching');
    if (autoCaching === 'false') {
      // Don't process queue if auto-caching is disabled
      BandwidthLimitedDownloadService.downloadQueue = [];
      return;
    }
    
    // Mark as processing
    BandwidthLimitedDownloadService.isProcessingQueue = true;
    
    try {
      // Process queue until we hit time or bandwidth limit
      while (
        BandwidthLimitedDownloadService.downloadQueue.length > 0 && 
        (deadline.timeRemaining() > 100 || deadline.didTimeout)
      ) {
        const nextDoc = BandwidthLimitedDownloadService.downloadQueue[0];
        
        try {
          // Check if document is already cached
          const isCached = await DocumentService.isDocumentCached(nextDoc.id);
          if (isCached) {
            // Already cached, remove from queue
            BandwidthLimitedDownloadService.downloadQueue.shift();
            continue;
          }
          
          // Check file size first
          const fileRef = ref(storage, nextDoc.path);
          const metadata = await getMetadata(fileRef);
          const fileSize = metadata.size || 0;
          
          // Check if we can download this file within our limit
          if (!BandwidthLimitedDownloadService.canDownloadInBackground(fileSize)) {
            console.log(`[BandwidthLimitedDownloadService] Daily limit reached, stopping queue processing`);
            break;
          }
          
          // Get download URL
          const url = await getDownloadURL(fileRef);
          
          // Fetch with no-cors to avoid triggering downloads
          const response = await fetch(url, { 
            method: 'GET',
            cache: 'no-store',
            headers: {
              'Content-Type': 'application/octet-stream'
            },
            credentials: 'same-origin'
          });
          
          // Get the binary data
          const blob = await response.blob();
          
          // Store directly in IndexedDB using DocumentService
          await DocumentService.storeBlobInIndexedDB(nextDoc.id, blob);
          
          // Update document metadata to mark as cached
          await DocumentService.updateDocumentCacheStatus(nextDoc.id);
          
          // Track bandwidth usage
          BandwidthLimitedDownloadService.trackUsage(fileSize);
          
          console.log(`[BandwidthLimitedDownloadService] Downloaded: ${nextDoc.name} (${fileSize} bytes)`);
        } catch (error) {
          console.error(`[BandwidthLimitedDownloadService] Error downloading ${nextDoc.name}:`, error);
        }
        
        // Remove from queue regardless of success/failure (we can try again later)
        BandwidthLimitedDownloadService.downloadQueue.shift();
      }
    } finally {
      // Mark as no longer processing
      BandwidthLimitedDownloadService.isProcessingQueue = false;
      
      // Schedule next run if there are still items in the queue
      if (
        BandwidthLimitedDownloadService.downloadQueue.length > 0 && 
        'requestIdleCallback' in window
      ) {
        window.requestIdleCallback(BandwidthLimitedDownloadService.processDownloadQueue);
      }
    }
  },
  
  /**
   * Download a specific document (user-initiated, not subject to bandwidth limits)
   * @param {Object} document Document to download
   * @returns {Promise<boolean>} Success status
   */
  downloadDocument: async (document) => {
    try {
      console.log(`[BandwidthLimitedDownloadService] User-initiated download: ${document.name}`);
      
      // Check if already cached
      const isCached = await DocumentService.isDocumentCached(document.id);
      console.log(`[BandwidthLimitedDownloadService] Document cached check: ${isCached}`);
      
      if (isCached) {
        console.log(`[BandwidthLimitedDownloadService] Document already cached: ${document.name}`);
        return true;
      }
      
      const fileRef = ref(storage, document.path);
      const url = await getDownloadURL(fileRef);
      
      // Fetch the document
      const response = await fetch(url, { 
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/octet-stream'
        },
        credentials: 'same-origin'
      });
      
      // Get the binary data
      const blob = await response.blob();
      console.log(`[BandwidthLimitedDownloadService] Downloaded blob size: ${blob.size}`);
      
      // Store in IndexedDB
      const storedResult = await DocumentService.storeBlobInIndexedDB(document.id, blob);
      console.log(`[BandwidthLimitedDownloadService] Blob storage result: ${storedResult}`);
      
      // Update document metadata
      const updateResult = await DocumentService.updateDocumentCacheStatus(document.id);
      console.log(`[BandwidthLimitedDownloadService] Cache status update result: ${updateResult}`);
      
      // Verify it was stored correctly
      const verifyCache = await DocumentService.isDocumentCached(document.id);
      console.log(`[BandwidthLimitedDownloadService] Verify cache after download: ${verifyCache}`);
      
      // Track bandwidth (for analytics only, not limited)
      const fileSize = blob.size || 0;
      BandwidthLimitedDownloadService.trackUsage(fileSize);
      
      console.log(`[BandwidthLimitedDownloadService] User-initiated download completed: ${document.name}`);
      return true;
    } catch (error) {
      console.error(`[BandwidthLimitedDownloadService] Error in user-initiated download: ${document.path}`, error);
      return false;
    }
  },
  
  /**
   * Queue all documents in a section for background download
   * @param {string} sectionPath Section path
   * @param {Array} documents List of documents in the section
   */
  queueSectionDocuments: (sectionPath, documents) => {
    // Check if auto-caching is disabled
    const autoCaching = localStorage.getItem('autoCaching');
    if (autoCaching === 'false') {
      // Don't queue if auto-caching is disabled
      return;
    }
    
    if (!documents || documents.length === 0) return;
    
    console.log(`[BandwidthLimitedDownloadService] Queueing ${documents.length} documents from ${sectionPath}`);
    
    // Queue each document
    documents.forEach(doc => {
      BandwidthLimitedDownloadService.queueForDownload(doc);
    });
  }
};

export default BandwidthLimitedDownloadService;
